// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { MyRound } from './types';

type Options = {
  apiBaseUrl: string;
  token: string | null | undefined;
};

export type MyRoundsErrorCode = 'noPlayer' | 'loadFailed';

type Result = {
  rounds: MyRound[] | null;
  loading: boolean;
  error: MyRoundsErrorCode | null;
  reload: () => void;
};

/** Loads the rounds the signed-in player has played, newest first. */
export function useMyRounds({ apiBaseUrl, token }: Options): Result {
  const [rounds, setRounds] = useState<MyRound[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<MyRoundsErrorCode | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  useEffect(() => {
    if (!token) {
      setRounds(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`${apiBaseUrl}/api/player/rounds`, { headers, signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(String(response.status));
        }
        return (await response.json()) as MyRound[];
      })
      .then((payload) => {
        if (controller.signal.aborted) return;
        setRounds(Array.isArray(payload) ? payload : []);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setRounds(null);
        setError(err instanceof Error && err.message === '404' ? 'noPlayer' : 'loadFailed');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [apiBaseUrl, headers, reloadToken, token]);

  return { rounds, loading, error, reload };
}
